import React, { useState, useRef } from 'react';
import authService from '../services/authService';

interface ResetPasswordPageProps {
  token: string;
  onNavigateToLogin: () => void;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ token, onNavigateToLogin }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);

  const showErr = (msg: string) => {
    setError(msg);
    setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!password || !confirm) { showErr('Tous les champs sont obligatoires.'); return; }
    if (password.length < 8) { showErr('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (password !== confirm) { showErr('Les mots de passe ne correspondent pas.'); return; }
    try {
      setIsLoading(true);
      await authService.resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      const msg = err.message || 'Erreur lors de la réinitialisation.';
      showErr(msg.includes('invalide') || msg.includes('Invalid') || msg.includes('expiré') ? 'Lien invalide ou expiré. Refaites une demande.' : msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Evoclick</h1>
          <h2 className="text-2xl font-semibold text-gray-700">Nouveau mot de passe</h2>
        </div>

        {/* Passerelle vers l'application mobile : le lien du mail ouvre le
            site, ce bandeau permet de poursuivre dans l'app si elle est
            installee. S'efface de lui-même une fois le mot de passe change,
            et reste sans effet si l'application est absente. */}
        {!success && (
          <a
            href={`evoclick://reset-password?token=${encodeURIComponent(token)}`}
            className="mb-4 flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-left transition-colors hover:bg-violet-100"
          >
            <span className="text-xl">📱</span>
            <span className="flex-1">
              <span className="block text-sm font-semibold text-violet-800">
                Continuer dans l'application
              </span>
              <span className="block text-xs text-violet-600">
                Si vous avez installé EvoClick sur ce téléphone
              </span>
            </span>
            <span className="text-violet-400">›</span>
          </a>
        )}

        <div className="bg-white py-8 px-6 shadow-lg rounded-xl">
          {success ? (
            <div className="text-center space-y-4">
              <div className="text-5xl">✅</div>
              <p className="text-gray-800 font-semibold">Mot de passe modifié !</p>
              <button
                onClick={onNavigateToLogin}
                className="w-full py-3 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Se connecter
              </button>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div ref={errorRef} className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); if (error) setError(''); }}
                    className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-3 text-gray-400 hover:text-gray-600">
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Minimum 8 caractères</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmer le mot de passe</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => { setConfirm(e.target.value); if (error) setError(''); }}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Modification...' : 'Modifier le mot de passe →'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
